import React, { useState } from "react";
import { useSelector } from "react-redux";
import * as dateFns from "date-fns";

// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import {
  DataTableMessages,
  GlobalMessages,
  WaterAdditionHistoryMessages,
  VehiclesStatesTypesMessages
} from "@shared/languages/interfaces";
import { TLanguages } from "@shared/languages/languageData";
// endregion Imports - Languages
// region Imports - Interfaces
import { WaterAdditionReport } from "@shared/interfaces/water-addition.interface";
import { ActiveVehicleState } from "@shared/interfaces/active-vehicle-state.interface";
// endregion Imports - Interfaces
// region Imports - Utils
import utils from "@utils/useful-functions";
// endregion Imports - Utils
// region Imports - Components
import Header from "@components/Header";
import NewDataTable, { DataTableColumns, DataTableButtons } from "@molecules/NewDataTable";
import QueryFilterWaterAdditionHistory from "@organisms/QueryFilterWaterAdditionHistory";
// endregion Imports - Components
// region Imports - Styles
import { ScreenPlatform } from "@store/ducks/Screen/screen.type";
import { Container } from "./styles";
// endregion Imports - Styles

interface WaterAdditionHistoryProps {
  hideHeader?: boolean;
}

const WaterAdditionHistory: React.FC<WaterAdditionHistoryProps> = ({ hideHeader = false }) => {

  // region States
  const [waterAdditionData, setWaterAdditionData] = useState<WaterAdditionReport[]>([]);
  const [openFilterFields, setOpenFilterFields] = useState(true);
  const [numberOfFilteredOptions, setNumberOfFilteredOptions] = useState(0);

  // Used to create export file name
  const [startDate, setStartDate] = useState<string>(
    dateFns.format(dateFns.subDays(new Date(), 1), "yyyy-MM-dd").concat("T00:00")
  );
  const [finishDate, setFinishDate] = useState<string>(
    dateFns.format(new Date(), "yyyy-MM-dd").concat("T23:59")
  );

  const getTranslatedState = (state?: ActiveVehicleState): string => {
    if (!state) return "";

    const stateId = state.tvst_id_vehicle_state_type;
    const description = state.tvst_description || "";

    if (stateId && VehiclesStatesTypesMessages[stateId]) {
      return t(VehiclesStatesTypesMessages[stateId]);
    }

    return description;
  };

  // endregion States
  // region Hooks
  const { screen } = useSelector((screen) => screen);
  const { t, i18n } = useTranslation();
  /* endregion Hooks */
  // region Constants
  const dataTableSettings: DataTables.Settings = {
    order: [[2, "desc"]],
    columnDefs: [{ className: "dt-center", targets: -1 }]
  };
  const dataTableColumns: DataTableColumns[] = [
    { // Fleet
      title: t(WaterAdditionHistoryMessages.fleetCode),
      data: (data: WaterAdditionReport) => data.fleet_code || "",
      filterable: true,
      propertyName: "fleet_code"
    },
    { // Plate
      title: t(WaterAdditionHistoryMessages.licensePlate),
      data: (data: WaterAdditionReport) => data.license_plate || "",
      filterable: true,
      propertyName: "license_plate"
    },
    { // Start Date
      title: t(WaterAdditionHistoryMessages.startDate),
      data: (data: WaterAdditionReport) => {
        const date = data.rwa_start_date;

        return date ? dateFns.format(new Date(date), "dd/MM/yyyy HH:mm") : "";
      },
      filterable: true,
      propertyName: "rwa_start_date"
    },
    { // End Date
      title: t(WaterAdditionHistoryMessages.finishDate),
      data: (data: WaterAdditionReport) => {
        const date = data.rwa_finish_date;

        return date ? dateFns.format(new Date(date), "dd/MM/yyyy HH:mm") : "";
      },
      filterable: true,
      propertyName: "rwa_finish_date"
    },
    { // Duration
      title: t(GlobalMessages.duration),
      data: (data: WaterAdditionReport) => {
        if (data.rwa_start_date) {
          const endDate = data.rwa_finish_date ? new Date(data.rwa_finish_date) : new Date();
          const durationObj = utils.calcDataRange(new Date(data.rwa_start_date), endDate);

          return utils.formatDateIfHave(durationObj, "durationDescriptiveTime") || "--";
        }

        return "--";
      },
      filterable: false
    },
    { // Driver
      title: t(WaterAdditionHistoryMessages.driver),
      data: (data: WaterAdditionReport) => data.driver_name || "",
      filterable: true,
      propertyName: "driver_name"
    },
    { // Doc.
      title: t(WaterAdditionHistoryMessages.document),
      data: (data: WaterAdditionReport) => data.document_number || "",
      filterable: true,
      propertyName: "document_number"
    },
    { // Vehicle State
      title: t(WaterAdditionHistoryMessages.vehicleState),
      data: (data: WaterAdditionReport) => getTranslatedState(data.active_state),
      filterable: true,
      propertyName: "active_state"
    },
    { // Water Added
      title: t(WaterAdditionHistoryMessages.waterAdded),
      data: (data: WaterAdditionReport) => {
        const amount = data.rwa_water_added || 0;

        // the unit is stated once in the column title, so rows carry only the number
        return utils.formatNumberToLocaleString(amount, i18n.language as TLanguages, 2);
      },
      filterable: true,
      propertyName: "water_added"
    },
    { // Moment
      title: t(WaterAdditionHistoryMessages.moment),
      data: (data: WaterAdditionReport) => {
        if (!data.moment) return "";

        return data.moment === "PRE_DISCHARGE"
          ? t(WaterAdditionHistoryMessages.preDischarge)
          : t(WaterAdditionHistoryMessages.postDischarge);
      },
      filterable: true,
      propertyName: "moment"
    },
    { // Adress
      title: t(WaterAdditionHistoryMessages.address),
      data: (data: WaterAdditionReport) => data.address || "",
      filterable: true,
      propertyName: "address"
    }
  ];
  const dataTableButtons: DataTableButtons[] = [
    {
      name: t(DataTableMessages.buttonsPrint),
      key: "print",
      callback: () => utils.clickButtonDomElement("button-print"),
      extend: "print",
      className: "button-print",
      exportOptions: {
        columns: "th:not(:last-child)"
      }
    },
    {
      name: t(DataTableMessages.buttonsExport),
      callback: () => utils.clickButtonDomElement("button-export"),
      extend: "csv",
      key: "export",
      fieldSeparator: ";",
      className: "button-export",
      filename: `relatorio_adicoes_agua_${startDate}__${finishDate}`,
      exportOptions: {
        columns: "th:not(:last-child)"
      }
    }
  ];
  // endregion Constants
  // region Functions

  /**
   * Handles the application of a filter to manage and update the state with the
   * filtered options and their associated data.
   * @param {number} numberOfFilteredOptions - The total number of options that satisfy the filter criteria.
   * @param {WaterAdditionReport[]} returnedData - The array of filtered water addition report data that meets the specified criteria.
   * @param {string} startDate - The starting date of the applied filter range in string format.
   * @param {string} finishDate - The ending date of the applied filter range in string format.
   */
  const handleApplyFilter = (
    numberOfFilteredOptions: number,
    returnedData: WaterAdditionReport[],
    startDate: string,
    finishDate: string
  ) => {
    setNumberOfFilteredOptions(numberOfFilteredOptions);
    setWaterAdditionData(returnedData);
    setStartDate(startDate);
    setFinishDate(finishDate);
  };

  // endregion Functions

  return (
    <>
      {!hideHeader && <Header title={t(WaterAdditionHistoryMessages.title)} />}
      <Container platform={screen.platform as ScreenPlatform} className={hideHeader ? "" : "page"}>
        <NewDataTable
          data={waterAdditionData}
          columns={dataTableColumns}
          settings={dataTableSettings}
          buttons={dataTableButtons}
          filters
          onClickFilterButton={() => setOpenFilterFields(true)}
          numberOfFilteredOptions={numberOfFilteredOptions}
        />
        <QueryFilterWaterAdditionHistory
          open={openFilterFields}
          onClose={() => setOpenFilterFields(false)}
          onFilter={handleApplyFilter}
        />
      </Container>
    </>
  );
};

export default WaterAdditionHistory;
